import React, { useState, useEffect } from 'react';
import { Card, Collapse, IconButton, Grid, TableCell, TableRow } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import StageExpandableTable from '../tabels/StageExpandableTable';
import Icon from '@mui/material/Icon';
import { grey } from '@mui/material/colors';
import PropTypes from 'prop-types';
import ProductList from './ProductList';
import DiffRenderer from './DiffRenderer';
import { useVisibility } from 'context/visibilityContext'; // Import visibility hook

const NestedItemGroup = ({ title, items, sObjectName, expandedStates, onToggle, parentOrg, showSameFields }) => {
  const hasItems = items && Object.values(items).some((arr) => arr.length > 0);
  if (!hasItems) return null;

  return (
    <ArgonBox mt={2} pl={2} sx={{ borderLeft: `2px solid ${grey[200]}` }}>
      <ArgonTypography variant="subtitle2" fontWeight="bold" color="secondary" mb={1}>
        {title}
      </ArgonTypography>
      {['new', 'changed', 'deleted', 'same'].map((type) =>
        items[type]?.map((item) => (
          <NestedItem
            key={item.id}
            item={item}
            sObjectName={sObjectName}
            expandedStates={expandedStates}
            onToggle={onToggle}
            parentOrg={parentOrg}
            showSameFields={showSameFields}
          />
        ))
      )}
    </ArgonBox>
  );
};

const NestedItem = ({ item, sObjectName, expandedStates, onToggle, parentOrg, showSameFields }) => {
  const isExpanded = !!expandedStates[item.id];
  const nestedArrays = Object.entries(item).filter(
    ([key, value]) => value && typeof value === 'object' && value.hasOwnProperty('new')
  );

  return (
    <Card variant="outlined" sx={{ mt: 1 }}>
      <ArgonBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => onToggle(item.id)}
        sx={{ cursor: 'pointer', p: 1 }}
      >
        <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
          {item.item}
        </ArgonTypography>
        <IconButton size="small">
          <Icon>{isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
        </IconButton>
      </ArgonBox>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <ArgonBox p={2} bgcolor={grey[50]}>
          <DiffRenderer
            changes={item.diffs}
            sObjectName={sObjectName}
            orgType={parentOrg.type.toLowerCase()}
            showSameFields={showSameFields}
          />
          {nestedArrays.map(([key, value]) => {
            const nestedSObjectName =
              {
                productDecompEnrichmentRules: 'ProductDecompEnrichmentRule',
                valueTransformationGroup: 'ValueTransformationGroup',
                valueTransformations: 'ValueTransformation',
              }[key] || key.charAt(0).toUpperCase() + key.slice(1);
            return (
              <NestedItemGroup
                key={key}
                title={key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                items={value}
                sObjectName={nestedSObjectName}
                expandedStates={expandedStates}
                onToggle={onToggle}
                parentOrg={parentOrg}
                showSameFields={showSameFields}
              />
            );
          })}
        </ArgonBox>
      </Collapse>
    </Card>
  );
};

function DecompositionReview({ selectedOrg, productData, showSameFields }) {
  const [expandedStates, setExpandedStates] = useState({});
  const [productDataMap, setProductDataMap] = useState({});
  const [selectedProductId, setSelectedProductId] = useState(null);
  const { visibilityConfig } = useVisibility();

  const columns = [
    { name: 'item', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];

  // This function recursively processes the data tree
  const processDiffArray = (diffArray, sObjectName) => {
    const config = visibilityConfig.rc?.find((c) => c.objectName === sObjectName);
    const displayKeys = config?.displayColumns
      ? config.displayColumns.split(',').map((s) => s.trim())
      : ['name', 'Name'];

    const result = { new: [], changed: [], deleted: [], same: [] };
    ['new', 'changed', 'deleted', 'same'].forEach((type) => {
      result[type] = (diffArray?.[type] || []).map((item) => {
        const fields = item.fields || item;
        const displayName = displayKeys
          .map((key) => fields[key])
          .filter((v) => v != null)
          .join(' - ');

        const processedItem = {
          id: item.recordId || item.id || `${sObjectName}-${Math.random()}`,
          item:
            displayName ||
            `${sObjectName.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())} - ${item.recordId}`,
          diffs: item.changes,
        };

        // --- FIX: Dynamically find and process all nested diff objects ---
        Object.keys(item).forEach((key) => {
          if (item[key] && typeof item[key] === 'object' && item[key].hasOwnProperty('new')) {
            const nestedSObjectName =
              {
                productDecompEnrichmentRules: 'ProductDecompEnrichmentRule',
                valueTransformationGroup: 'ValueTransformationGroup',
                valueTransformations: 'ValueTransformation',
              }[key] || key.charAt(0).toUpperCase() + key.slice(1);
            processedItem[key] = processDiffArray(item[key], nestedSObjectName);
          }
        });

        return processedItem;
      });
    });
    return result;
  };

  useEffect(() => {
    if (!productData || productData.length === 0) {
      setProductDataMap({});
      return;
    }
    const generatedProductDataMap = {};
    productData.forEach((productDiff) => {
      const orchestrator = productDiff.orchestrator || {};
      const decompRules = JSON.parse(JSON.stringify(orchestrator.productFulfillmentDecompRules || {}));

      // --- FIX: Pre-process the data to handle nested arrays that are not standard diff objects ---
      const wrapNestedArrays = (diffObject) => {
        if (!diffObject) return;
        ['new', 'changed', 'deleted', 'same'].forEach((type) => {
          (diffObject[type] || []).forEach((item) => {
            // Recurse for enrichment rules first
            if (item.productDecompEnrichmentRules) {
              wrapNestedArrays(item.productDecompEnrichmentRules);
            }

            // Check and wrap valueTransformationGroup if it's a direct array
            if (item.valueTransformationGroup && Array.isArray(item.valueTransformationGroup)) {
              const groupArray = item.valueTransformationGroup;
              item.valueTransformationGroup = {
                new: type === 'new' ? groupArray : [],
                changed: type === 'changed' ? groupArray : [],
                deleted: type === 'deleted' ? groupArray : [],
                same: type === 'same' ? groupArray : [],
              };
            }
            // Add similar checks for other potential anomalies if necessary
          });
        });
      };

      wrapNestedArrays(decompRules);

      generatedProductDataMap[productDiff.product.sfId || productDiff.product.id] = {
        productName: productDiff.product.name,
        decompRules: processDiffArray(decompRules, 'ProductFulfillmentDecompRule'),
      };
    });
    setProductDataMap(generatedProductDataMap);
    if (productData.length > 0) {
      setSelectedProductId(productData[0].product.sfId || productData[0].product.id);
    }
  }, [productData, visibilityConfig]);

  const handleToggleExpand = (id) => {
    setExpandedStates((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderExpandedContent = (row) => {
    const isExpanded = !!expandedStates[row.id];
    // --- FIX: Dynamically find ALL nested arrays on the current row object ---
    const nestedArrays = Object.entries(row).filter(
      ([key, value]) => value && typeof value === 'object' && value.hasOwnProperty('new')
    );

    return (
      <TableRow>
        <TableCell colSpan={columns.length} style={{ padding: '0px' }}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <ArgonBox p={3} bgcolor={grey[50]}>
              <DiffRenderer
                changes={row.diffs}
                sObjectName={'ProductFulfillmentDecompRule'}
                orgType={selectedOrg.type.toLowerCase()}
                showSameFields={showSameFields}
              />
              {/* Render ALL found nested item groups */}
              {nestedArrays.map(([key, value]) => {
                const nestedSObjectName =
                  {
                    productDecompEnrichmentRules: 'ProductDecompEnrichmentRule',
                    valueTransformationGroup: 'ValueTransformationGroup',
                    valueTransformations: 'ValueTransformation',
                  }[key] || key.charAt(0).toUpperCase() + key.slice(1);
                return (
                  <NestedItemGroup
                    key={key}
                    title={key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                    items={value}
                    sObjectName={nestedSObjectName}
                    expandedStates={expandedStates}
                    onToggle={handleToggleExpand}
                    parentOrg={selectedOrg}
                    showSameFields={showSameFields}
                  />
                );
              })}
            </ArgonBox>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const renderProductSection = (productName, data) => {
    const { decompRules } = data;
    const renderTableSection = (title, rows) => {
      if (!rows || rows.length === 0) return null;
      const formattedRows = rows.map((row) => ({
        ...row,
        item: (
          <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
            {row.item}
          </ArgonTypography>
        ),
        onClick: () => handleToggleExpand(row.id),
        selected: !!expandedStates[row.id],
        actions: (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleExpand(row.id);
            }}
          >
            <Icon>{expandedStates[row.id] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
          </IconButton>
        ),
      }));
      return (
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                {title}
              </ArgonTypography>
              <StageExpandableTable
                rows={formattedRows}
                columns={columns}
                renderRowSubComponent={({ row }) => renderExpandedContent(row)}
              />
            </ArgonBox>
          </Card>
        </ArgonBox>
      );
    };
    const hasAnyData = ['new', 'changed', 'deleted', 'same'].some((type) => decompRules[type]?.length > 0);
    if (!hasAnyData)
      return (
        <Card>
          <ArgonBox p={3} textAlign="center">
            <ArgonTypography>No decomposition data available.</ArgonTypography>
          </ArgonBox>
        </Card>
      );
    return (
      <ArgonBox key={productName}>
        {['new', 'changed', 'deleted', 'same'].map(
          (type) =>
            decompRules[type]?.length > 0 &&
            renderTableSection(`${type.charAt(0).toUpperCase() + type.slice(1)} Decomposition Rules`, decompRules[type])
        )}
      </ArgonBox>
    );
  };

  const productList = Object.keys(productDataMap).map((id) => ({ id, name: productDataMap[id].productName }));
  const handleProductSelect = (productId) => {
    setSelectedProductId(productId);
    setExpandedStates({});
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <ProductList
          products={productList}
          selectedProductId={selectedProductId}
          onProductSelect={handleProductSelect}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        {selectedProductId && productDataMap[selectedProductId] ? (
          renderProductSection(productDataMap[selectedProductId].productName, productDataMap[selectedProductId])
        ) : (
          <Card>
            <ArgonBox p={3} textAlign="center">
              <ArgonTypography>Select a product to view decomposition details.</ArgonTypography>
            </ArgonBox>
          </Card>
        )}
      </Grid>
    </Grid>
  );
}

DecompositionReview.propTypes = {
  productData: PropTypes.array.isRequired,
  showSameFields: PropTypes.bool,
  selectedOrg: PropTypes.object.isRequired,
};

export default DecompositionReview;
